import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import SelectClear from '@atlaskit/icon/core/migration/cross-circle--select-clear';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ExpandCollapseToggle } from '@atlassian/jira-servicedesk-cmdb-expand-collapse-toggle/src/ui/index.tsx';
import type { Messages, RenderType, ValueType } from '../../../types/index.tsx';
import messages from './messages.tsx';

export type Props<T> = {
	isEditing: boolean;
	value: ValueType<T>;
	render: RenderType<T>;
	messages: Messages;
	onHandleDelete: (value: ValueType<T>) => void;
};

export const CollapsibleCard = <T,>(props: Props<T>) => {
	const {
		render: { HeaderContent, DetailContent },
		isEditing,
		onHandleDelete,
		messages: customMessages,
	} = props;
	const { formatMessage } = useIntl();

	const [isExpanded, setIsExpanded] = useState<boolean>(false);

	const handleExpandCollapseClick = () => {
		setIsExpanded(!isExpanded);
	};

	const renderChevronButton = () => {
		const buttonLabel = formatMessage(isExpanded ? messages.showLessDetails : messages.showDetails);
		return (
			<ExpandCollapseToggle
				isExpanded={isExpanded}
				label={buttonLabel}
				onClick={handleExpandCollapseClick}
				testId="issue-field-service-entity.common.ui.collapsible-field.card.collapse-toggle"
			/>
		);
	};

	const renderDeleteButton = (value: ValueType<T>) => {
		const buttonLabel = formatMessage(customMessages.removeItem);
		return (
			<Tooltip content={buttonLabel}>
				<DeleteButtonWrapper>
					<Button
						appearance="subtle"
						onClick={() => onHandleDelete(value)}
						iconBefore={<SelectClear spacing="spacious" label={buttonLabel} />}
						testId="issue-field-service-entity.common.ui.collapsible-field.card.delete-button"
						interactionName="auto-generated.issue-field-service-entity.common.ui.collapsible-field.card.delete-button.button"
					/>
				</DeleteButtonWrapper>
			</Tooltip>
		);
	};

	return (
		<Container>
			<Header>
				<HeaderContentWrapper>
					<HeaderContent value={props.value} isEditing={isEditing} isExpanded={isExpanded} />
				</HeaderContentWrapper>
				<CardActions>
					{isEditing ? renderDeleteButton(props.value) : null}
					{renderChevronButton()}
				</CardActions>
			</Header>
			{isExpanded && <DetailContent value={props.value} isEditing={isEditing} />}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	position: 'relative',
	width: '100%',

	backgroundColor: token('elevation.surface.raised'),
	boxShadow: token('elevation.shadow.raised'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	marginBottom: token('space.100'),
	paddingTop: token('space.150'),
	paddingRight: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContentWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${gridSize * 5}px)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardActions = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginTop: token('space.negative.050'),
	marginBottom: token('space.negative.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		marginLeft: token('space.100'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 4}px`,
		margin: 0,
		padding: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeleteButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& button:hover': {
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		svg: {
			color: token('color.icon.danger'),
		},
	},
});
